import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  X, 
  Fingerprint, 
  Lock, 
  ShieldCheck, 
  AlertCircle, 
  Loader2, 
  ArrowLeft, 
  Check, 
  Smartphone,
  KeyRound
} from "lucide-react";
import { useAuth, ADMIN_USERNAME } from "./AuthContext";
import { useAuthFlow } from "./useAuthFlow";


export default function AuthModal() {
  const {
    isAuthModalOpen,
    setAuthModalOpen,
    screen,
    setScreen,
    googleEmail,
    setGoogleEmail,
    githubUsername,
    setGithubUsername,
    adminPassword,
    setAdminPassword,
    mfaCode,
    setError,
    loading,
    error,
    passkeyStep,
    setPasskeyStep,
    qrCodeUrl,
    mfaInputRef,
    handleGoogleSubmit,
    startGooglePasskeyScan,
    handleGithubSubmit,
    startGithubPasskeyScan,
    handleAdminCredsSubmit,
    handleAdminMfaSubmit,
    handleMfaChange
  } = useAuthFlow();

  if (!isAuthModalOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Dark overlay with backdrop blur */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setAuthModalOpen(false)}
        className="absolute inset-0 bg-brand-charcoal/80 backdrop-blur-sm"
      />

      {/* Modal Card */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-brand-cream border-4 border-brand-burgundy rounded-sm w-full max-w-md overflow-hidden relative shadow-2xl z-10 text-brand-charcoal"
      >
        {/* Header decoration block */}
        <div className="bg-brand-burgundy text-white px-5 py-3.5 flex justify-between items-center select-none border-b border-brand-orange/30">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-brand-orange" />
            <span className="font-mono text-xs uppercase font-bold tracking-wider">
              Secure Auth Terminal
            </span>
          </div>
          <button 
            onClick={() => setAuthModalOpen(false)}
            className="text-brand-sand hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            
            {/* SCREEN 1: Main Provider Selection */}
            {screen === "main" && (
              <motion.div
                key="main"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                <div className="text-center">
                  <h3 className="text-xl font-bold font-display tracking-tight">Select Authentication Protocol</h3>
                  <p className="text-xs text-stone-500 mt-1 font-light">
                    Access administrative views or client-side workspaces.
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  {/* Google Login Trigger */}
                  <button
                    onClick={() => setScreen("google")}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-stone-200 hover:border-brand-burgundy rounded-sm shadow-sm transition-all hover:translate-y-[-1px] group"
                  >
                    <div className="flex items-center gap-3">
                      {/* Google G Logo Inline SVG */}
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          fill="#EA4335"
                          d="M12.24 10.285V14.4h6.887c-.648 2.41-2.519 4.114-5.187 4.114-3.513 0-6.36-2.847-6.36-6.36s2.847-6.36 6.36-6.36c1.6 0 3.05.59 4.17 1.57l3.07-3.07C18.66 1.76 15.65 1 12.24 1 6.13 1 1.13 6 1.13 12.16s5 11.16 11.11 11.16c6.26 0 11.08-4.4 11.08-11.23 0-.6-.05-1.18-.15-1.74H12.24z"
                        />
                      </svg>
                      <span className="text-xs font-mono font-bold text-stone-700">Google Passwordless</span>
                    </div>
                    <span className="text-[10px] font-mono bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full group-hover:bg-brand-burgundy/10 group-hover:text-brand-burgundy transition-colors">
                      Passkey
                    </span>
                  </button>

                  {/* GitHub Login Trigger */}
                  <button
                    onClick={() => setScreen("github")}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-stone-200 hover:border-brand-burgundy rounded-sm shadow-sm transition-all hover:translate-y-[-1px] group"
                  >
                    <div className="flex items-center gap-3">
                      {/* GitHub Logo Inline SVG */}
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.164 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                      </svg>
                      <span className="text-xs font-mono font-bold text-stone-700">GitHub Developer Auth</span>
                    </div>
                    <span className="text-[10px] font-mono bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full group-hover:bg-brand-burgundy/10 group-hover:text-brand-burgundy transition-colors">
                      Security Key
                    </span>
                  </button>

                  <div className="relative py-2 flex items-center justify-center">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200"></div></div>
                    <span className="relative bg-brand-cream px-3 font-mono text-[9px] text-stone-400 uppercase font-bold tracking-wider">or</span>
                  </div>

                  {/* Admin credentials Trigger */}
                  <button
                    onClick={() => setScreen("admin_creds")}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand-sand border-2 border-brand-sand hover:border-brand-burgundy rounded-sm shadow-sm transition-all hover:bg-brand-sand/60"
                  >
                    <KeyRound className="w-4 h-4 text-brand-burgundy" />
                    <span className="text-xs font-mono font-bold text-brand-burgundy uppercase tracking-wider">Admin Console Sign In</span>
                  </button>
                </div>
              </motion.div>
            )}

            {/* SCREEN 2: Google Email Input & Passkey Handshake */}
            {screen === "google" && (
              <motion.div
                key="google"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => { setScreen("main"); setPasskeyStep("idle"); }} className="text-stone-500 hover:text-brand-burgundy" aria-label="Back to main">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <span className="font-mono text-xs text-stone-500 font-bold uppercase">Google Passkey</span>
                </div>

                {passkeyStep === "idle" ? (
                  <form onSubmit={handleGoogleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="google-email" className="block text-[10px] font-mono text-stone-700 uppercase tracking-widest mb-1.5 font-bold">Google Workspace Email</label>
                      <input
                        id="google-email"
                        type="email"
                        required
                        value={googleEmail}
                        onChange={(e) => setGoogleEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-sm font-mono text-xs focus:border-brand-burgundy focus:outline-none"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-brand-burgundy hover:bg-brand-burgundy/90 text-white font-mono text-xs uppercase tracking-wider font-bold py-3 rounded-sm shadow-md transition-colors"
                    >
                      Authenticate with Passkey
                    </button>
                  </form>
                ) : (
                  <div className="bg-white border border-stone-200 rounded p-6 flex flex-col items-center justify-center text-center space-y-4 shadow-inner min-h-[200px]">
                    {passkeyStep === "prompt" && (
                      <>
                        <Fingerprint className="w-12 h-12 text-brand-orange animate-bounce" />
                        <div>
                          <h4 className="text-sm font-bold">Passkey Authentication Prompt</h4>
                          <p className="text-[11px] text-stone-500 mt-1 max-w-[250px] mx-auto font-light leading-normal">
                            Activate your secure biometric key or security device linked to <strong>{googleEmail}</strong>.
                          </p>
                        </div>
                        <button
                          onClick={startGooglePasskeyScan}
                          className="px-5 py-2 bg-brand-orange hover:bg-brand-amber text-white font-mono text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors"
                        >
                          Verify Identity
                        </button>
                      </>
                    )}

                    {passkeyStep === "scanning" && (
                      <>
                        <div className="relative flex items-center justify-center">
                          <div className="absolute w-16 h-16 rounded-full border-2 border-brand-orange/30 animate-ping" />
                          <Fingerprint className="w-12 h-12 text-brand-orange" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold flex items-center gap-1.5 justify-center">
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-orange" />
                            Validating Biometrics...
                          </h4>
                          <p className="text-[10px] text-stone-400 mt-1 font-mono">
                            Verifying TPM key signature payload
                          </p>
                        </div>
                      </>
                    )}

                    {passkeyStep === "success" && (
                      <>
                        <div className="w-12 h-12 rounded-full bg-brand-green/10 border-2 border-brand-green flex items-center justify-center">
                          <Check className="w-6 h-6 text-brand-green" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-brand-green">Authentication Verified</h4>
                          <p className="text-[10px] text-stone-400 mt-1 font-mono">
                            Session initialized successfully
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* SCREEN 3: GitHub Username Input & Passkey Handshake */}
            {screen === "github" && (
              <motion.div
                key="github"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => { setScreen("main"); setPasskeyStep("idle"); }} className="text-stone-500 hover:text-brand-burgundy" aria-label="Back to main">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <span className="font-mono text-xs text-stone-500 font-bold uppercase">GitHub Developer Auth</span>
                </div>

                {passkeyStep === "idle" ? (
                  <form onSubmit={handleGithubSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="github-username" className="block text-[10px] font-mono text-stone-700 uppercase tracking-widest mb-1.5 font-bold">GitHub Handle / Email</label>
                      <input
                        id="github-username"
                        type="text"
                        required
                        value={githubUsername}
                        onChange={(e) => setGithubUsername(e.target.value)}
                        placeholder="github_dev"
                        className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-sm font-mono text-xs focus:border-brand-burgundy focus:outline-none"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-brand-burgundy hover:bg-brand-burgundy/90 text-white font-mono text-xs uppercase tracking-wider font-bold py-3 rounded-sm shadow-md transition-colors"
                    >
                      Authenticate with Security Key
                    </button>
                  </form>
                ) : (
                  <div className="bg-white border border-stone-200 rounded p-6 flex flex-col items-center justify-center text-center space-y-4 shadow-inner min-h-[200px]">
                    {passkeyStep === "prompt" && (
                      <>
                        <Fingerprint className="w-12 h-12 text-brand-orange animate-bounce" />
                        <div>
                          <h4 className="text-sm font-bold">FIDO2 Hardware Key Challenge</h4>
                          <p className="text-[11px] text-stone-500 mt-1 max-w-[250px] mx-auto font-light leading-normal">
                            Insert your YubiKey or touch your device's built-in secure key for user <strong>{githubUsername}</strong>.
                          </p>
                        </div>
                        <button
                          onClick={startGithubPasskeyScan}
                          className="px-5 py-2 bg-brand-orange hover:bg-brand-amber text-white font-mono text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors"
                        >
                          Send FIDO2 Request
                        </button>
                      </>
                    )}

                    {passkeyStep === "scanning" && (
                      <>
                        <div className="relative flex items-center justify-center">
                          <div className="absolute w-16 h-16 rounded-full border-2 border-brand-orange/30 animate-ping" />
                          <Fingerprint className="w-12 h-12 text-brand-orange" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold flex items-center gap-1.5 justify-center">
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-orange" />
                            Reading Security Token...
                          </h4>
                          <p className="text-[10px] text-stone-400 mt-1 font-mono">
                            Verifying WebAuthn attestation signature
                          </p>
                        </div>
                      </>
                    )}

                    {passkeyStep === "success" && (
                      <>
                        <div className="w-12 h-12 rounded-full bg-brand-green/10 border-2 border-brand-green flex items-center justify-center">
                          <Check className="w-6 h-6 text-brand-green" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-brand-green">Security Key Accepted</h4>
                          <p className="text-[10px] text-stone-400 mt-1 font-mono">
                            Developer access token generated
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* SCREEN 4: Admin Credentials (Username/Password) */}
            {screen === "admin_creds" && (
              <motion.div
                key="admin_creds"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => setScreen("main")} className="text-stone-500 hover:text-brand-burgundy" aria-label="Back to main">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <span className="font-mono text-xs text-stone-500 font-bold uppercase">Admin Console</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="admin-username" className="block text-[10px] font-mono text-stone-700 uppercase tracking-widest mb-1.5 font-bold">Admin Username</label>
                    <input
                      id="admin-username"
                      type="text"
                      disabled
                      autoComplete="off"
                      value={ADMIN_USERNAME}
                      className="w-full px-3 py-2.5 bg-stone-100 border border-stone-200 text-stone-500 rounded-sm font-mono text-xs focus:outline-none cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label htmlFor="admin-password" className="block text-[10px] font-mono text-stone-700 uppercase tracking-widest mb-1.5 font-bold">Password</label>
                    <input
                      id="admin-password"
                      type="password"
                      required
                      autoComplete="one-time-code"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAdminCredsSubmit(e as any);
                        }
                      }}
                      placeholder="••••••••••••••••"
                      className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-sm font-mono text-xs focus:border-brand-burgundy focus:outline-none"
                    />
                  </div>

                  {error && (
                    <div className="bg-brand-burgundy/10 border-l-2 border-brand-burgundy p-3 flex items-start gap-2 text-brand-burgundy font-mono text-[10px] leading-normal">
                      <AlertCircle className="w-4 h-4 shrink-0 text-brand-burgundy" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleAdminCredsSubmit}
                    disabled={loading}
                    className={`w-full font-mono text-xs uppercase tracking-wider font-bold py-3 rounded-sm shadow-md transition-all flex items-center justify-center gap-2 ${
                      !loading
                        ? "bg-brand-burgundy hover:bg-brand-burgundy/90 text-white cursor-pointer"
                        : "bg-stone-100 text-stone-400 border border-stone-200 cursor-not-allowed"
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                        Verifying...
                      </>
                    ) : (
                      "Verify Credentials"
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {/* SCREEN 5: Admin MFA (Authy/TOTP Token Check) */}
            {screen === "admin_mfa" && (
              <motion.div
                key="admin_mfa"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setScreen("admin_creds"); setError(null); }} className="text-stone-500 hover:text-brand-burgundy" aria-label="Back to admin console">
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <span className="font-mono text-xs text-stone-500 font-bold uppercase">MFA Lockbox</span>
                  </div>
                  <span className="px-2 py-0.5 bg-brand-orange/10 text-brand-orange border border-brand-orange/30 font-mono text-[8px] uppercase tracking-wider rounded font-bold">
                    Authy Enabled
                  </span>
                </div>

                <div className="text-center bg-white border border-stone-200 rounded p-4 shadow-sm flex flex-col items-center">
                  <span className="text-[10px] font-mono text-brand-burgundy uppercase font-bold tracking-wider mb-2">
                    Pair Authenticator (First Time Setup)
                  </span>
                  
                  {/* Pair QR code server */}
                  <div className="border-4 border-brand-burgundy bg-brand-cream p-1 rounded-sm shadow-inner mb-3">
                    <img 
                      src={qrCodeUrl} 
                      alt="TOTP QR Code" 
                      width="120"
                      height="120"
                      className="block select-none"
                      onError={(e) => {
                        // Failover text secret display in case of network problems
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>

                  <div className="text-[9px] font-mono text-stone-500 text-center leading-normal">
                    Scan with Authy or any standard authenticator app.
                  </div>
                </div>
                <form onSubmit={handleAdminMfaSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="mfa-code" className="block text-[10px] font-mono text-stone-700 uppercase tracking-widest text-center mb-2.5 font-bold">
                      Enter 6-Digit MFA Verification Code
                    </label>
                    <div className="relative">
                      <Smartphone className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        id="mfa-code"
                        type="text"
                        ref={mfaInputRef}
                        required
                        pattern="\d{6}"
                        maxLength={6}
                        value={mfaCode}
                        onChange={handleMfaChange}
                        placeholder="000 000"
                        className="w-full pl-10 pr-3 py-3 bg-white border-2 border-stone-200 rounded-sm font-mono text-center text-lg tracking-[0.4em] focus:border-brand-burgundy focus:outline-none"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-brand-burgundy/10 border-l-2 border-brand-burgundy p-3 flex items-start gap-2 text-brand-burgundy font-mono text-[10px] leading-normal">
                      <AlertCircle className="w-4 h-4 shrink-0 text-brand-burgundy" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={mfaCode.length !== 6 || loading}
                    className={`w-full font-mono text-xs uppercase tracking-wider font-bold py-3 rounded-sm shadow-md transition-all flex items-center justify-center gap-2 ${
                      mfaCode.length === 6 && !loading
                        ? "bg-brand-burgundy hover:bg-brand-burgundy/90 text-white cursor-pointer"
                        : "bg-stone-100 text-stone-400 border border-stone-200 cursor-not-allowed"
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                        Decrypting MFA Protocol...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4" />
                        Establish Admin Session
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
